import React from "react";

const URL_RE = /(https?:\/\/[^\s]+)/g;

function splitWithUrls(text) {
  const parts = [];
  let last = 0;
  let match;
  const re = new RegExp(URL_RE.source, "g");
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ type: "text", value: text.slice(last, match.index) });
    parts.push({ type: "url", value: match[1] });
    last = match.index + match[1].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  return parts.length ? parts : [{ type: "text", value: text }];
}

function renderInline(text) {
  const nodes = [];
  let key = 0;
  const codeSplit = text.split(/(`[^`]+`)/g);
  codeSplit.forEach((segment) => {
    if (segment.startsWith("`") && segment.endsWith("`")) {
      nodes.push(
        <code key={key++} className="roi-agent-inline-code">
          {segment.slice(1, -1)}
        </code>
      );
      return;
    }
    const boldSplit = segment.split(/(\*\*[^*]+\*\*)/g);
    boldSplit.forEach((part) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        nodes.push(<strong key={key++}>{part.slice(2, -2)}</strong>);
        return;
      }
      splitWithUrls(part).forEach((chunk) => {
        if (chunk.type === "url") {
          nodes.push(
            <a
              key={key++}
              href={chunk.value}
              target="_blank"
              rel="noopener noreferrer"
              className="roi-agent-link"
            >
              {chunk.value}
            </a>
          );
        } else if (chunk.value) {
          nodes.push(<span key={key++}>{chunk.value}</span>);
        }
      });
    });
  });
  return nodes;
}

export function FormattedMessageContent({ content }) {
  if (!content) return null;
  const lines = String(content).split("\n");
  const blocks = [];
  let listItems = null;
  let listType = null;
  let listStart = 1;
  let key = 0;

  const flushList = () => {
    if (!listItems?.length) return;
    const Tag = listType === "ol" ? "ol" : "ul";
    blocks.push(
      <Tag key={key++} className="roi-agent-md-list" start={listType === "ol" ? listStart : undefined}>
        {listItems.map((item, i) => (
          <li key={i}>{renderInline(item)}</li>
        ))}
      </Tag>
    );
    listItems = null;
    listType = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    // Blank lines make a list loose; they do not start a new list at 1.
    if (!trimmed) return;
    const bullet = trimmed.match(/^[-*]\s+(.+)/);
    const numbered = trimmed.match(/^(\d+)\.\s+(.+)/);
    if (bullet) {
      if (listType !== "ul") {
        flushList();
        listType = "ul";
        listItems = [];
      }
      listItems.push(bullet[1]);
      return;
    }
    if (numbered) {
      if (listType !== "ol") {
        flushList();
        listType = "ol";
        listStart = Number(numbered[1]);
        listItems = [];
      }
      listItems.push(numbered[2]);
      return;
    }
    flushList();
    if (/^#{1,3}\s+/.test(trimmed)) {
      blocks.push(<h4 key={key++} className="roi-agent-md-heading">{renderInline(trimmed.replace(/^#{1,3}\s+/,''))}</h4>);
    } else {
      blocks.push(
        <p key={key++} className="roi-agent-md-p">
          {renderInline(line)}
        </p>
      );
    }
  });
  flushList();
  return <div className="roi-agent-md">{blocks}</div>;
}

export const TOOL_LABELS = {
  propose_calculator_logic: "Proposed calculation logic",
  verify_calculator_design_qa: "Checked calculator",
  read_calculator_source: "Reviewed source materials",
  digest_materials: "Reviewed attachments",
  list_calculators: "Listed existing calculators",
  fetch_figma_design: "Fetched Figma design inventory",
  propose_calculator_mockup: "Generated calculator blueprint",
  commit_calculator_build: "Committed calculator build",
  get_calculator_schema: "Loaded calculator schema",
  calculate_roi_metrics: "Calculated ROI metrics",
  set_field_values: "Updated calculator fields",
  reset_calculator: "Reset calculator",
  generate_roi_report: "Prepared report",
  inspect_calculator_structure: "Inspected calculator structure",
  digest_excel: "Digested Excel / logic file",
  digest_figma: "Digested Figma design inventory",
};

const FIGMA_STATUS_LABELS = {
  ok: "Fetched Figma design inventory",
  warn: "Figma partial inventory (labels OK — continuing build)",
  error: "Figma rate limit — using Excel logic; retry Figma later",
  disk: "Figma design from local snapshot (no API call)",
};

export function toolDisplayName(name, outcome) {
  if (name === "fetch_figma_design" && outcome?.status) {
    if (outcome.detail === "figma_from_disk_cache") {
      return FIGMA_STATUS_LABELS.disk;
    }
    return FIGMA_STATUS_LABELS[outcome.status] || TOOL_LABELS.fetch_figma_design;
  }
  if (outcome?.status === "error" && TOOL_LABELS[name]) {
    return `${TOOL_LABELS[name]} (failed)`;
  }
  return TOOL_LABELS[name] || name.replace(/_/g, " ");
}

/** Merge API tool_outcomes with legacy tool_calls name list. */
export function normalizeToolOutcomes(toolCalls, toolOutcomes) {
  if (Array.isArray(toolOutcomes) && toolOutcomes.length) {
    return toolOutcomes;
  }
  return (toolCalls || []).map((name) => ({
    name: typeof name === "string" ? name : name?.name,
    status: "ok",
  }));
}

export function toolStatusClass(status) {
  if (status === "warn") return "roi-agent-tool-status is-warn";
  if (status === "error") return "roi-agent-tool-status is-error";
  return "roi-agent-tool-status is-ok";
}

function summarizeLogic(logic) {
  if (!logic || typeof logic !== "object") return "";
  const thenSteps = logic.then || logic.actions || [];
  if (!Array.isArray(thenSteps) || !thenSteps.length) return "";
  const formulaLine = thenSteps
    .filter(
      (t) =>
        t?.variable != null &&
        String(t.variable).trim() &&
        t?.operator != null &&
        String(t.operator).trim() &&
        t?.value !== undefined &&
        t?.value !== null &&
        String(t.value).trim() !== ""
    )
    .map((t) => `${t.variable} ${t.operator} ${t.value ?? ""}`.trim())
    .join("; ");
  if (formulaLine) return formulaLine;
  return thenSteps
    .map((t) => {
      if (t.expression) return t.expression;
      if (t.variable && t.operator) {
        return `${t.variable} ${t.operator} ${t.value ?? ""}`.trim();
      }
      return "";
    })
    .filter(Boolean)
    .join("; ");
}

/** Normalize formula / logic rules from API mockups (LLM shapes vary). */
export function mockupFormulaRules(mockup) {
  if (!mockup) return [];
  const raw =
    mockup.logic_rules ??
    mockup.calculation_rules ??
    mockup.formulas ??
    mockup.rules ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map((r, i) => {
    if (typeof r === "string") {
      return { name: r, summary: "" };
    }
    const logic = r.logic || r.roi_calculation_logic || r.formula;
    const formulaLine = r.formula_line || summarizeLogic(logic);
    const summaryText = r.summary || r.description || "";
    return {
      name:
        r.roi_logic_name ||
        r.name ||
        r.title ||
        r.rule_name ||
        `(unnamed formula ${i + 1})`,
      summary: summaryText,
      formulaLine,
      displayDetail: summaryText.startsWith("Excel:")
        ? summaryText.replace(/^Excel:\s*/, "").trim()
        : summaryText && formulaLine && !summaryText.includes(formulaLine)
          ? `${summaryText} — ${formulaLine}`
          : summaryText || formulaLine,
    };
  });
}

export function mockupContentPages(mockup) {
  if (!mockup?.pages?.length) return [];
  return mockup.pages;
}

export function initialAssistantMessage(mode) {
  if (mode === "builder") {
    return "Tell me what you’d like to calculate. We can define the inputs and formulas together, then create a draft for your review. Workbooks and design files are optional.";
  }
  return "Tell me your assumptions or ask me to explain ROI results.";
}
